<script setup>
/* eslint-disable */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router';
import { useMarketsStore } from '@/stores/markets';
import { useLiveSportsStore } from '@/stores/live_sports';
import { useFixtureStore } from '@/stores/fixture';
import { useDatesStore } from '@/stores/dates';
import { useFavouritesStore } from '@/stores/favourites';
import { useInitializeStore } from '@/stores/initialize';
import { useDateTimeSportFiltersStore } from '@/stores/date_time_sport_filters';
import { useBetBoosterStore, findBoostedOutcome } from '@/stores/bet_booster';
import OverviewOdds from '@/components/pre_sports/highlights/OverviewOdds.vue'

const router = useRouter();
const marketsStore = useMarketsStore();
const liveSportsStore = useLiveSportsStore();
const fixtureStore = useFixtureStore();
const datesStore = useDatesStore();
const favouritesStore = useFavouritesStore();
const initializeStore = useInitializeStore();
const dateTimeSportFiltersStore = useDateTimeSportFiltersStore();
const betBoosterStore = useBetBoosterStore();

import commonSvgs from '@/assets/svgs/commonSvgs.json'
import JerseyIcon from '@/assets/svgs/jerseyIcon.vue';
const svg_data = ref(commonSvgs);

const props = defineProps({
	primary_overview_market: {
		required: true,
	},
	secondary_overview_market: {
		required: true,
	},
	fixture: {
		type: Object,
		required: true,
	},
	show_competition_region: {
		type: Boolean,
		default: false,
	}
});

const toggleFavourite = () => {
	favouritesStore.toggleFixture(props.fixture.id);
}

const goToFixture = () => {
	router.push({ name: 'pre-sports-fixturebook', params: { sport_id: props.fixture.sport_id, region_id: props.fixture.region_id, competition_id: props.fixture.competition_id, fixture_id: props.fixture.id } } )
}

const primaryOverviewMarketOdds = computed(() => {
	if (!props.fixture.overview_markets.hasOwnProperty(props.primary_overview_market.slug)) return [];

	return props.fixture.overview_markets[props.primary_overview_market.slug];
});

const secondaryOverviewMarketOdds = computed(() => {
	if (!props.fixture.overview_markets.hasOwnProperty(props.secondary_overview_market.slug)) return [];

	return props.fixture.overview_markets[props.secondary_overview_market.slug];
});

const isFavouite = computed(() => {
	if (favouritesStore.favouriteFixtureIds.includes(props.fixture.id)) return true;
});

const isBetBoostsRoute = computed(() => router.currentRoute.value.name === 'pre-sports-bet-boosts');

const fixtureBoost = computed(() => {
	if (!isBetBoostsRoute.value || !props.fixture) return null;

	const activePrimarySlug = props.primary_overview_market?.slug;
	const activeSecondarySlug = props.secondary_overview_market?.slug;

	// 1. Check in betBoosterStore.boosted_bets for this fixture matching active overview markets
	if (betBoosterStore.boosted_bets && betBoosterStore.boosted_bets.length) {
		const matching = betBoosterStore.boosted_bets.filter((b) => {
			if (b.fixture_id !== props.fixture.id) return false;
			if (!b.market_slug) return true;
			return b.market_slug === activePrimarySlug || b.market_slug === activeSecondarySlug;
		});

		const bestMatch = matching.reduce((best, curr) => {
			if (!curr.boost_percentage) return best;
			if (!best || curr.boost_percentage > best.boost_percentage) return curr;
			return best;
		}, null);

		if (bestMatch && bestMatch.boost_percentage) {
			return {
				boost_percentage: bestMatch.boost_percentage,
				is_super_boost: bestMatch.is_super_boost
			};
		}
	}

	// 2. Fallback: inspect ONLY the currently displayed overview markets for boosted outcomes
	const displayedMarkets = {};
	if (activePrimarySlug && props.fixture.overview_markets?.[activePrimarySlug]) {
		displayedMarkets[activePrimarySlug] = props.fixture.overview_markets[activePrimarySlug];
	}
	if (activeSecondarySlug && props.fixture.overview_markets?.[activeSecondarySlug]) {
		displayedMarkets[activeSecondarySlug] = props.fixture.overview_markets[activeSecondarySlug];
	}

	const outcome = findBoostedOutcome(displayedMarkets);
	if (outcome && outcome.boosted_price != null && outcome.price != null) {
		const originalOdds = Number(outcome.price);
		const boostedOdds = Number(outcome.boosted_price);
		if (!isNaN(originalOdds) && !isNaN(boostedOdds) && originalOdds > 0) {
			const diff = boostedOdds - originalOdds;
			const pct = Math.round((diff / originalOdds) * 100);
			return {
				boost_percentage: pct,
				is_super_boost: pct >= 20
			};
		}
	}

	return null;
});

</script>

<template>

	<div class="live-info-inner upcomimg_sport">

		<div class="no-live-teams" >
			<div v-if="show_competition_region && (props.fixture.region_name || props.fixture.competition_name)" class="region-info">
				<div class="fixture-competition-region">
					<img v-if="props.fixture.region_icon_url || props.fixture.region_icon" class="region-icon" :src="'/assets' + (props.fixture.region_icon_url || props.fixture.region_icon)" alt="" />
					<span>{{ props.fixture.region_name ? props.fixture.region_name + ' - ' : '' }}{{ props.fixture.competition_name }}</span>
				</div>
			</div>
			<div class="live-teams">
				<div class="star-stats" :class="{ 'competition-region': show_competition_region }">
					<div class="star-ico" :class="{ 'star-ico-small': props.fixture.stats_id && props.fixture.stats_id != '' && initializeStore.screenWidth > 767 }" @click="toggleFavourite">
						<svg xmlns="http://www.w3.org/2000/svg" width="12" height="11.143" viewBox="0 0 12 11.143">
							<path :style="{ fill: isFavouite ? 'var(--primary)' : '' }" id="Icon_ionic-ios-star" :class="`fixture-${fixture?.id}`" data-name="Icon ionic-ios-star" d="M12.67,5.545H8.729l-1.2-3.573a.434.434,0,0,0-.814,0l-1.2,3.573H1.554a.43.43,0,0,0-.429.429.315.315,0,0,0,.008.072.412.412,0,0,0,.179.3L4.551,8.63,3.308,12.244a.43.43,0,0,0,.147.482.414.414,0,0,0,.241.1.525.525,0,0,0,.268-.1l3.161-2.253,3.161,2.253a.5.5,0,0,0,.268.1.385.385,0,0,0,.238-.1.425.425,0,0,0,.147-.482L9.7,8.63l3.212-2.3.078-.067a.449.449,0,0,0,.139-.287A.454.454,0,0,0,12.67,5.545Z" transform="translate(-1.125 -1.688)" fill="var(--tab-gray)" />
						</svg>
					</div>
					<div v-if="props.fixture.stats_id && props.fixture.stats_id != '' && initializeStore.screenWidth > 767" class="statistics-button">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="var(--light-gray)" d="M332.8 320h38.4c6.4 0 12.8-6.4 12.8-12.8V172.8c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v134.4c0 6.4 6.4 12.8 12.8 12.8zm96 0h38.4c6.4 0 12.8-6.4 12.8-12.8V76.8c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v230.4c0 6.4 6.4 12.8 12.8 12.8zm-288 0h38.4c6.4 0 12.8-6.4 12.8-12.8v-70.4c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v70.4c0 6.4 6.4 12.8 12.8 12.8zm96 0h38.4c6.4 0 12.8-6.4 12.8-12.8V108.8c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v198.4c0 6.4 6.4 12.8 12.8 12.8zM496 384H64V80c0-8.8-7.2-16-16-16H16C7.2 64 0 71.2 0 80v336c0 17.7 14.3 32 32 32h464c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16z"/></svg>
					</div>
				</div>
				<div class="team-name" @click="goToFixture">

					<div class="team-inner">
						<span>{{ datesStore.preFixtureBookDate(props.fixture.start_datetime) }}</span>
						<div class="playing-teams">
							<img
								v-if="fixtureStore.getParticipantLogo(props.fixture.participant_logos)"
								class="playing-team-logo"
								:src="fixtureStore.getParticipantLogo(props.fixture.participant_logos).home" alt="#">
								<JerseyIcon v-if="fixtureStore.getParticipantJersey(props.fixture.jersey_colors)" :jerseyColor="fixtureStore.getParticipantJersey(props.fixture.jersey_colors).home" />
							<p>{{ props.fixture.participants ? fixture.participants[0] : '' }}</p>
						</div>
					</div>
					<div class="team-inner">
						<span>{{ datesStore.preFixtureBookTime(props.fixture.start_datetime) }}</span>
						<div class="playing-teams">
							<img
								v-if="fixtureStore.getParticipantLogo(props.fixture.participant_logos	)"
								class="playing-team-logo"
								:src="fixtureStore.getParticipantLogo(props.fixture.participant_logos).away" alt="#">
							<JerseyIcon v-if="fixtureStore.getParticipantJersey(props.fixture.jersey_colors)" :jerseyColor="fixtureStore.getParticipantJersey(props.fixture.jersey_colors).away" />
							<p v-if="!liveSportsStore.hasOneParticipant(fixture.sport_id) && !fixture.outright">
								{{ props.fixture.participants ? fixture.participants[1] : '' }}</p>
						</div>
					</div>

				</div>

				<div v-if="fixtureBoost" class="fixture-boost-badge desktop-only-badge" :class="{ 'super-boost': fixtureBoost.is_super_boost }">
					<span class="boost-badge-icon">🚀</span>
					<span class="boost-badge-text">+{{ fixtureBoost.boost_percentage }}%</span>
				</div>
			</div>


			<!-- <div @click="goToFixture" class="betting-total-mobile">
				<p><img src="/assets/icons/plus-bets.svg" alt="">&nbsp;{{ props.	fixture.odds_count }} {{$t('account.affiliate.bets')}}
				</p>
			</div> -->

		</div>

		<OverviewOdds v-if="primary_overview_market != null" :type="'primary'" :overview_market="primary_overview_market" :fixture="props.fixture" :fixture_overview_markets="props.fixture.overview_markets" :add_margin="show_competition_region"/>
		<OverviewOdds v-if="secondary_overview_market != null" :type="'secondary'" :overview_market="secondary_overview_market" :fixture="props.fixture" :fixture_overview_markets="props.fixture.overview_markets" :show_totals="true" :add_margin="show_competition_region"/>
		<!-- mobile-live icon start -->
		<div class="mobile-bottom-live">
			<div class="info-inner">
				<div class="upcoming-date-mobile">
					<span>{{ datesStore.preFixtureBookDate(props.fixture.start_datetime) }}</span>
					<span>{{ datesStore.preFixtureBookTime(props.fixture.start_datetime) }}</span>
					<span v-if="show_competition_region || dateTimeSportFiltersStore.sort == 'time'"> | {{ props.fixture.region_name }} - {{ props.fixture.competition_name}} </span>
					<div v-if="fixtureBoost" class="fixture-boost-badge mobile-only-badge" :class="{ 'super-boost': fixtureBoost.is_super_boost }">
						<span class="boost-badge-icon">🚀</span>
						<span class="boost-badge-text" style="color: var(--white);">+{{ fixtureBoost.boost_percentage }}%</span>
					</div>
				</div>
				<div v-if="props.fixture.stats_id && props.fixture.stats_id != ''" class="statistics-button-mobile">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="var(--light-gray)" d="M332.8 320h38.4c6.4 0 12.8-6.4 12.8-12.8V172.8c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v134.4c0 6.4 6.4 12.8 12.8 12.8zm96 0h38.4c6.4 0 12.8-6.4 12.8-12.8V76.8c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v230.4c0 6.4 6.4 12.8 12.8 12.8zm-288 0h38.4c6.4 0 12.8-6.4 12.8-12.8v-70.4c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v70.4c0 6.4 6.4 12.8 12.8 12.8zm96 0h38.4c6.4 0 12.8-6.4 12.8-12.8V108.8c0-6.4-6.4-12.8-12.8-12.8h-38.4c-6.4 0-12.8 6.4-12.8 12.8v198.4c0 6.4 6.4 12.8 12.8 12.8zM496 384H64V80c0-8.8-7.2-16-16-16H16C7.2 64 0 71.2 0 80v336c0 17.7 14.3 32 32 32h464c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16z"/></svg>
				</div>

			</div>
			<div @click="goToFixture" class="betting-total-mobile">
				<p><img src="/assets/icons/plus-bets.svg" alt="" />&nbsp;{{ props.fixture.odds_count }} {{$t('account.affiliate.bets')}}</p>
			</div>
		</div>
		<!-- mobile-live icon start -->

	</div>


</template>
<style scoped>
@media (hover: hover) {
	.live-bet-dashboard .live-tab-info .live-info-outer .live-info-inner:nth-child(n+2):hover {
		background: var(--light-dark);
		border-radius: 3px;
	}
}
@media (hover: none) {
  .live-bet-dashboard .live-tab-info .live-info-outer .live-info-inner:nth-child(n+2):hover {
    background: none; /* Remove background */
    border-radius: none; /* Remove border-radius */
  }
}
@media screen and (min-width: 768px) {
	.live-bet-dashboard.round-games-main .live-tab-info .live-info-outer .live-info-inner .live-teams .team-name .team-inner p {
		max-width: 20ch;
	}
}

.playing-teams{
	display: flex;
	align-items: center;
	gap: 0.5rem;
	justify-content: flex-start;
}

.playing-teams img {

	width: 1rem;
	height: 1rem;
	margin: 0 0.2rem;

}

.statistics-button {
	height: 14px;
	width: 14px;
}

.statistics-button-mobile {
	margin-top: -3px;
	height: 14px;
	width: 14px;
}

.star-stats {
	display: flex;
	gap: 0.5rem;
	flex-direction: column;
	justify-content: center;
	align-self: center;
	height: auto;
}

.star-ico-small {
	padding-bottom: 0px !important;
	height: 14px;
	width: 14px;
}

@media screen and (min-width: 320px) and (max-width: 767px) {
	.upcomimg_sport {
		padding-right: 12px !important;
		padding-left: 12px !important;
	}
}

.live-bet-dashboard.round-games-main .live-tab-info .live-info-outer .live-info-inner .mobile-bottom-live {
	grid-template-columns: 2px 1fr auto;
}
.live-bet-dashboard.round-games-main .live-tab-info .live-info-outer .live-info-inner .mobile-bottom-live .info-inner {
	display: grid;
    grid-template-columns: 90% 10%;
    align-items: flex-start;
}

.statistics-button-mobile {
	align-self: anchor-center;
}

.region-info {
	padding: 4px 0 4px 26px;
	display: flex;
	align-items: center;
}

html[dir="rtl"] .region-info {
	padding-left: 0;
	padding-right: 26px;
}

@media screen and (max-width: 767px) {
	.region-info {
		display: none !important;
	}
}

.fixture-competition-region {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	font-weight: 700;
	color: var(--primary);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	letter-spacing: 0.2px;
}

.fixture-competition-region .region-icon {
	width: 14px;
	height: 14px;
	object-fit: cover;
	flex-shrink: 0;
	border-radius: 50%;
}

.fixture-boost-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	padding: 3px 8px;
	border-radius: 4px;
	background: var(--primary);
	color: var(--white);
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.2px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	justify-self: end;
	margin-right: 12px;
	white-space: nowrap;
	flex-shrink: 0;
	pointer-events: none;
}

.fixture-boost-badge.super-boost {
	background: linear-gradient(135deg, var(--secondary) 0%, var(--odd-price-down) 100%);
	box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35);
}

.fixture-boost-badge .boost-badge-icon {
	font-size: 11px;
	line-height: 1;
}

.fixture-boost-badge .boost-badge-text {
	font-size: 11px;
	font-weight: 800;
	color: var(--white);
	line-height: 1;
}

.fixture-boost-badge.desktop-only-badge {
	display: inline-flex;

	@media screen and (max-width: 991px) {
		display: none !important;
	}
}

.fixture-boost-badge.mobile-only-badge {
	display: none;

	@media screen and (max-width: 767px) {
		display: inline-flex !important;
		margin-left: 6px;
		margin-right: 0;
		padding: 1px 5px;
		font-size: 10px;
		vertical-align: middle;
	}
}

.upcoming-date-mobile {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
}
</style>